import React from 'react';
import { Grid, Typography, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { displayLongSentences } from '../../../utils/utils';
import SingleUser from './SingleUser';
import { AssetType } from '../../../redux/activity/activitySlice';
import { IChatList } from '../../../redux/chat/chatSlice';

export const StyledGrid = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '7px',
  padding: '16px',
  backgroundColor: '#fff',
  borderRadius: '10px',
  border: '1px solid var(--Accent-Light-Steel-Blue, #EEE)',
  maxWidth: '384px',
}));

interface INotificationProps {
  chat: IChatList;
  toastId: string;
}

function Notification({ chat, toastId }: INotificationProps) {
  const { t } = useTranslation();

  const handleClose = (id: string) => {
    toast.dismiss(id);
  };

  return (
    <StyledGrid>
      <SingleUser
        profilePic={chat.client.profilePic}
        firstName={chat.client.firstName}
        lastName={chat.client.lastName}
        hideStatus
      />
      <Grid container direction="column" gap={1}>
        <Grid item container alignItems="center">
          <Grid gap={1} display="flex" flexDirection="row">
            <Typography variant="calloutRegular" color="primary">
              {chat.client.firstName} {chat.client.lastName}
            </Typography>
            {chat.client.archived && <img src="/assets/icons/archive.svg" alt="archive" />}
          </Grid>
        </Grid>
        {chat.chatMessages[0].messageType === AssetType.TEXT && (
          <Typography variant="meta" color="primary">
            {displayLongSentences(chat.chatMessages[0].message, 75)}
          </Typography>
        )}
        {chat.chatMessages[0].messageType === AssetType.IMAGE && (
          <Grid display="flex" flexDirection="row" gap="4px" alignItems="center">
            <img src="/assets/icons/photo.svg" alt="message" height={20} width={20} />
            <Typography variant="meta" color="primary">
              {t('chat.photo')}
            </Typography>
          </Grid>
        )}
        {chat.chatMessages[0].messageType === AssetType.VIDEO && (
          <Grid display="flex" flexDirection="row" gap="4px" alignItems="center">
            <img src="/assets/icons/video-camera.svg" alt="message" height={24} width={24} />
            <Typography variant="meta" color="primary">
              {t('chat.video')}
            </Typography>
          </Grid>
        )}
        {chat.chatMessages[0].messageType === AssetType.AUDIO && (
          <Grid display="flex" flexDirection="row" gap="4px" alignItems="center">
            <img src="/assets/icons/voice.svg" alt="message" height={24} width={24} />
            <Typography variant="meta" color="primary">
              {t('chat.audio')}
            </Typography>
          </Grid>
        )}
        {chat.chatMessages[0].messageType === AssetType.DOCUMENT && (
          <Grid display="flex" flexDirection="row" gap="4px" alignItems="center">
            <img src="/assets/icons/file-text.svg" alt="message" height={20} width={20} />
            <Typography variant="meta" color="primary">
              {t('chat.document')}
            </Typography>
          </Grid>
        )}
      </Grid>
      <Grid onClick={() => handleClose(toastId)}>
        <img
          src="/assets/icons/close-circle-large.svg"
          alt="close-notification"
          width={20}
          height={20}
          style={{ cursor: 'pointer' }}
        />
      </Grid>
    </StyledGrid>
  );
}

export const showNotificationToast = (chat: IChatList) => {
  if (!chat) return;
  if (!chat.client) return;
  toast.custom((t) => <Notification chat={chat} toastId={t.id} />, {
    duration: 5000,
  });
};

export default showNotificationToast;
